import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Quill, replaceNbsp } from '../../patches/PatchedQuill';
import { editorFormats, editorToolbar, makeParsedRichText } from '../../utils/richText';
import { useBiAction } from '../BiTracking';
import { TokenPicker } from '../TokenText/TokenPicker/TokenPicker';
import { TokenToolbarButton } from '../TokenText/TokenToolbarButton/TokenToolbarButton';
import { Typography } from '../Typography';
import styles from './RichTextEditor.module.scss';
import { GetRichTextEditorTID } from './tokens/tokenTestIds';
import { useTokenizedQuill } from './tokens/useTokenizedQuill';
export const theme = 'snow';
export const extractPureTextFromRichText = function extractPureTextFromRichText() {
    let richText = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : '';
    const quill = new Quill(document.createElement('div'), {
        modules: {
            toolbar: false
        },
        theme: 'snow'
    });
    applyValueToQuill(quill, richText);
    return quill.getText();
};
function sanitizeDelta(parsed) {
    const delta = parsed.value;
    if (!('ops' in delta)) {
        return delta;
    }
    // This cleans up &nbsp; characters that have already been saved in the database.
    delta.ops.forEach((op)=>{
        if (op.insert && typeof op.insert === 'string') {
            op.insert = replaceNbsp(op.insert);
        }
    });
    return delta;
}
export const applyValueToQuill = (quill, value)=>{
    if (value) {
        if (typeof value === 'object') {
            // We assume it's a rich-text object.
            quill.setContents(sanitizeDelta(value));
        } else {
            try {
                // Maybe its a stringified rich-text object.
                const parsed = JSON.parse(value);
                if (parsed.value) {
                    quill.setContents(sanitizeDelta(parsed));
                } else {
                    // Sometimes we can parse a string of pure text. For example, when it contains only digits.
                    // In that case, we want to assume its pure text.
                    quill.setText(value);
                }
            } catch (err) {
                // We assume its pure text.
                quill.setText(value);
            }
        }
    } else {
        quill.setText('');
    }
};
export function BaseRichTextEditor(props) {
    var _tokens_adornment_testId;
    const { editorHeight, placeholder, initialValue = '', error, helperText, className, isReadOnly, disabled = false, editorRef, onChange, toolbarActions, helperTextReserveSpace, tokens, alignmentEnabled = false, testId, bi, noBi: _noBi } = props, rest = _object_without_properties(props, [
        "editorHeight",
        "placeholder",
        "initialValue",
        "error",
        "helperText",
        "className",
        "isReadOnly",
        "disabled",
        "editorRef",
        "onChange",
        "toolbarActions",
        "helperTextReserveSpace",
        "tokens",
        "alignmentEnabled",
        "testId",
        "bi",
        "noBi"
    ]);
    const logBiAction = useBiAction();
    const instance = useRef({
        onChange
    });
    instance.current = {
        onChange
    };
    const contentOnFocusRef = useRef('');
    const rootRef = useRef(null);
    const quillRef = useRef(null);
    const toolbarActionsRef = useRef(null);
    const hasQuill = useRef(false);
    const lastAppliedValueRef = useRef(undefined);
    const isUpdatingRef = useRef(false);
    const [editorEl, setEditorEl] = useState(null);
    const [quillReady, setQuillReady] = useState(false);
    const getQuill = useCallback(()=>quillRef.current, []);
    const tokenized = useTokenizedQuill({
        editorEl,
        getQuill,
        tokens,
        quillReady
    });
    const { RichTextEditorTestIds } = useMemo(()=>GetRichTextEditorTID(testId), [
        testId
    ]);
    useEffect(()=>{
        if (!hasQuill.current && editorEl) {
            const quill = new Quill(editorEl, {
                modules: {
                    toolbar: editorToolbar(alignmentEnabled)
                },
                placeholder: placeholder || '',
                theme,
                // Important for tooltips not to open beyond the editor bounds and behave
                // well when the editor is in a modal.
                bounds: rootRef.current,
                formats: editorFormats(alignmentEnabled),
                readOnly: isReadOnly || disabled
            });
            quillRef.current = quill;
            hasQuill.current = true;
            if (testId) {
                quill.root.setAttribute('data-testid', testId + '-input');
            }
            applyValueToQuill(quill, initialValue);
            lastAppliedValueRef.current = initialValue;
            quill.on('text-change', ()=>{
                if (!isUpdatingRef.current) {
                    const newContent = quill.getContents();
                    const parsedContent = makeParsedRichText(newContent);
                    instance.current.onChange && instance.current.onChange(parsedContent);
                }
            });
            quill.root.addEventListener('focus', ()=>{
                contentOnFocusRef.current = quill.getText();
            });
            quill.root.addEventListener('blur', ()=>{
                if (bi === null || bi === void 0 ? void 0 : bi.name) {
                    logBiAction({
                        entityType: 'rich_text_editor',
                        entityName: bi.name,
                        actionType: 'text_input',
                        testId: testId !== null && testId !== void 0 ? testId : '',
                        data: _object_spread_props(_object_spread({}, bi.data), {
                            has_changed: contentOnFocusRef.current !== quill.getText()
                        })
                    });
                }
            });
            // Mark Quill as ready after a brief delay to ensure toolbar is rendered
            setTimeout(()=>{
                setQuillReady(true);
            }, 0);
        }
    }, [
        editorEl,
        testId,
        initialValue,
        isReadOnly,
        disabled,
        placeholder
    ]);
    // Update Quill editor readOnly state when isReadOnly or disabled prop changes
    useEffect(()=>{
        if (quillRef.current && hasQuill.current) {
            quillRef.current.enable(!isReadOnly && !disabled);
        }
    }, [
        isReadOnly,
        disabled
    ]);
    // Update Quill editor when initialValue changes externally (e.g., from form.setValue)
    useEffect(()=>{
        if (quillRef.current && hasQuill.current) {
            const normalizeForComparison = (val)=>{
                if (!val) return '';
                if (typeof val === 'string') {
                    try {
                        const parsed = JSON.parse(val);
                        return JSON.stringify(parsed);
                    } catch (unused) {
                        return val;
                    }
                }
                return JSON.stringify(val);
            };
            const lastAppliedNormalized = normalizeForComparison(lastAppliedValueRef.current);
            const newNormalized = normalizeForComparison(initialValue);
            // Skip if the value hasn't actually changed
            if (lastAppliedNormalized === newNormalized) {
                return;
            }
            // Check if the current editor content matches the new value
            // If it does, the user likely typed it, so don't reset the editor
            const currentContent = quillRef.current.getContents();
            const currentParsed = makeParsedRichText(currentContent);
            const currentNormalized = normalizeForComparison(currentParsed);
            // Only update if the new value is different from current editor content
            // This means it's an external update (like form.setValue), not user typing
            if (currentNormalized !== newNormalized) {
                isUpdatingRef.current = true;
                applyValueToQuill(quillRef.current, initialValue);
                lastAppliedValueRef.current = initialValue;
                // Reset the flag after a brief delay to allow the update to complete
                setTimeout(()=>{
                    isUpdatingRef.current = false;
                }, 0);
            } else {
                // Editor content matches new value (user typed it), just update the ref
                lastAppliedValueRef.current = initialValue;
            }
        }
    }, [
        initialValue
    ]);
    const showTokenAdornment = Boolean((tokens === null || tokens === void 0 ? void 0 : tokens.adornment) && !tokens.adornment.hidden);
    const hasToolbarContent = Boolean(toolbarActions) || showTokenAdornment;
    const tokenAdornment = (tokens === null || tokens === void 0 ? void 0 : tokens.adornment) && !tokens.adornment.hidden ? /*#__PURE__*/ _jsx(TokenToolbarButton, _object_spread_props(_object_spread({}, tokens.adornment), {
        label: tokens.adornment.label,
        onClick: tokenized.openFrom,
        testId: (_tokens_adornment_testId = tokens.adornment.testId) !== null && _tokens_adornment_testId !== void 0 ? _tokens_adornment_testId : RichTextEditorTestIds.TokenAdornment()
    })) : null;
    const toolbarContent = hasToolbarContent ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            toolbarActions,
            tokenAdornment
        ]
    }) : null;
    // Separate effect to position toolbar actions after Quill creates the toolbar
    useEffect(()=>{
        if (hasToolbarContent && toolbarActionsRef.current && rootRef.current && quillReady) {
            const toolbar = rootRef.current.querySelector('.ql-toolbar');
            if (toolbar) {
                // Only move if not already in the toolbar
                if (toolbarActionsRef.current.parentElement !== toolbar) {
                    toolbar.appendChild(toolbarActionsRef.current);
                }
            }
        }
    }, [
        hasToolbarContent,
        quillReady
    ]);
    return /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({
        className: cn(styles.root, error && styles.error, disabled && styles.disabled, className),
        ref: rootRef,
        "data-testid": testId
    }, rest), {
        children: [
            /*#__PURE__*/ _jsx("div", {
                style: {
                    height: editorHeight || 200
                },
                ref: (el)=>{
                    editorRef && editorRef(el);
                    setEditorEl(el);
                }
            }),
            toolbarContent && /*#__PURE__*/ _jsx("div", {
                ref: toolbarActionsRef,
                className: styles.toolbarActions,
                children: toolbarContent
            }),
            tokens && /*#__PURE__*/ _jsx(TokenPicker, {
                config: tokens.picker,
                trigger: tokenized
            }),
            helperText && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                skin: error ? 'error' : 'secondary',
                className: cn({
                    [styles.helperText]: !helperTextReserveSpace
                }),
                testId: testId && testId + '-helper-text',
                children: helperText
            })
        ]
    }));
}
